import { useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { useBatch } from "./hooks";
import { effectiveLabel, provenanceLabel } from "./characteristic-labels";
import { originsLabel, originStatusLabel } from "./origin-labels";
import { BatchEditDialog } from "./BatchEditDialog";
import { OriginPendingNotice } from "./OriginPendingNotice";
import { OriginPicker } from "./OriginPicker";

/**
 * Spec 084 — a faixa da batelada no cartão da execução ativa: código, características efetivas com a
 * procedência em texto e o que falta informar.
 */
export function BatchStrip({ lineId, runId }: { lineId: string; runId: string }) {
  const query = useBatch(lineId, runId);
  const [editing, setEditing] = useState(false);
  const [pickingOrigin, setPickingOrigin] = useState(false);

  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        variant="inline"
      />
    );
  }
  const batch = query.data;
  if (!batch) return null;
  const editable = batch.canEditBatchCode || batch.characteristics.some((c) => c.editable);

  return (
    <section
      aria-label="Batelada"
      className="space-y-2 rounded-md border border-border-default px-3 py-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-text-tertiary">Batelada</span>
          <span className="font-mono text-[12px] text-text-primary">{batch.batchCode}</span>
          {batch.characteristics.some((c) => c.missingRequired) ? (
            <Pill tone="warning" withDot>
              Falta informar
            </Pill>
          ) : null}
          {batch.originStatus === "PENDING" && !batch.canEditOrigins ? (
            <Pill tone="warning" withDot>
              {originStatusLabel(batch.originStatus)}
            </Pill>
          ) : null}
        </div>
        {editable ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[12px]"
            onClick={() => setEditing(true)}
          >
            <Pencil size={12} className="mr-1" />
            Corrigir
          </Button>
        ) : null}
      </div>
      {batch.originStatus === "PENDING" && batch.canEditOrigins ? (
        <OriginPendingNotice lineId={lineId} batch={batch} />
      ) : batch.originStatus !== null ? (
        <p className="flex flex-wrap items-center gap-1.5 text-[12px] text-text-secondary">
          <span className="text-text-tertiary">Origem:</span>
          <span className="text-text-primary">{originsLabel(batch.origins)}</span>
          {batch.canEditOrigins ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px]"
              onClick={() => setPickingOrigin(true)}
            >
              Trocar
            </Button>
          ) : null}
        </p>
      ) : null}
      {batch.characteristics.length === 0 ? (
        <p className="text-[12px] text-text-tertiary">
          Nenhuma característica de batelada nesta linha.
        </p>
      ) : (
        <dl className="grid gap-x-4 gap-y-1 text-[12px] sm:grid-cols-2">
          {batch.characteristics.map((c) => (
            <div key={c.characteristicId} className="min-w-0">
              <dt className="text-text-tertiary">{c.name}</dt>
              <dd className="truncate text-text-primary">
                {effectiveLabel(c)}
                {provenanceLabel(c) ? (
                  <span className="text-text-tertiary"> · {provenanceLabel(c)}</span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {editing ? (
        <BatchEditDialog lineId={lineId} runId={runId} onClose={() => setEditing(false)} />
      ) : null}
      {pickingOrigin ? (
        <OriginPicker lineId={lineId} batch={batch} onClose={() => setPickingOrigin(false)} />
      ) : null}
    </section>
  );
}
